import { FormBuilderField } from 'types/form.type';
import {
  EntityBusinessType,
  EntityType,
  IzinUsahaType
} from '@xendit/id-open-banking-ekyb-common/dist/enums/submission/entity';
import { AccountPurposePurpose, Dati2, Province } from '@xendit/id-open-banking-ekyb-common/dist/enums/submission';
import {
  IzinUsahaTypeMap,
  EntityTypeMap,
  ProvinceMap,
  PrimaryIncomeSourceTypeMap,
  AccountPurposePurposeMap,
  EntityBusinessTypeMap,
  Dati2Map
} from 'helpers/utils/enumsNameMappings';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { t } from 'i18next';
import { PrimaryIncomeSourceType } from 'types/override.type';

export const Form2Section1Fields = (
  field: ControllerRenderProps<FieldValues, 'entity.izin_usaha'>
): FormBuilderField[] => [
  {
    label: 'Informasi Perusahaan',
    type: 'title'
  },
  {
    name: 'entity.name',
    label: 'Nama perusahaan (Sesuai akta)',
    type: 'text',
    placeholder: 'Nama perusahaan',
    fullWidth: true,
    validation: {
      required: 'Nama perusahaan harus diisi'
    }
  },
  {
    name: 'entity.npwp',
    label: 'Nomor NPWP Perusahaan',
    type: 'text',
    placeholder: 'Nomor NPWP',
    validation: {
      required: 'Nomor NPWP harus diisi',
      validate: {
        isNumber: (value: string) => !isNaN(Number(value)) || 'Pastikan format NPWP sesuai',
        minLength: (value: string) => value.length === 15 || 'Nomor NPWP harus terdiri dari 15 digit',
        isPositive: (value: string) => Number(value) > 0 || 'Pastikan format NPWP sesuai'
      }
    }
  },
  {
    name: 'companyLicense',
    label: 'Izin Usaha',
    type: 'group',
    children: [
      {
        name: 'entity.izin_usaha.type',
        label: 'Jenis Izin',
        defaultValue: '',
        placeholder: 'Pilih',
        type: 'autocomplete',
        fullWidth: true,
        span: 8,
        options: Object.keys(IzinUsahaType).map((item) => {
          return { label: IzinUsahaTypeMap[item as IzinUsahaType], value: item, key: item };
        }),
        validation: {
          required: 'Jenis Izin harus diisi'
        }
      },
      {
        name: 'entity.izin_usaha.number',
        label: 'Nomor Izin Usaha',
        placeholder: 'Nomor',
        fullWidth: true,
        type: 'text',
        span: 16,
        validation: {
          required: 'Nomor Izin Usaha harus diisi',
          validate: {
            isNumber: (value: string, formValues) => {
              if (formValues.entity.izin_usaha.type === IzinUsahaType.NIB.toString() && value) {
                return (!isNaN(Number(value)) && Number(value) > 0) || 'Pastikan format nomor sesuai';
              }
              return true;
            },
            pattern: (value: string, formValues) => {
              if (formValues.entity.izin_usaha.type === IzinUsahaType.NIB.toString() && value) {
                return /^[0-9]*$/.test(value) || 'Pastikan format nomor sesuai';
              }
              return true;
            }
          }
        }
      }
    ]
  },
  {
    name: 'entity.type.type',
    label: 'Tipe Perusahaan',
    type: 'autocomplete',
    defaultValue: '',
    placeholder: 'Pilih',
    options: Object.keys(EntityType).map((item) => {
      return { label: EntityTypeMap[item as EntityType], value: item, key: item };
    }),
    validation: {
      required: 'Tipe Perusahaan harus diisi'
    }
  },
  {
    conditional: {
      dependOn: 'entity.type.type',
      value: EntityType.OTHER
    },
    name: 'entity.type.description',
    type: 'text',
    placeholder: 'Alasan lainnya',
    validation: {
      required: 'Alasan lainnya harus diisi'
    }
  },
  {
    name: 'entity.business_type',
    label: 'Jenis Bisnis',
    type: 'autocomplete',
    defaultValue: '',
    placeholder: 'Pilih',
    options: Object.keys(EntityBusinessType).map((item) => {
      return { label: EntityBusinessTypeMap[item as EntityBusinessType], value: item, key: item };
    }),
    validation: {
      required: 'Jenis Bisnis harus diisi'
    }
  },
  {
    name: 'entity.nationality',
    label: 'Kewarganegaraan Perusahaan',
    type: 'text',
    disabled: true
  },
  {
    name: 'companyAddress',
    label: 'Alamat Legal Kantor',
    type: 'group',
    children: [
      {
        name: 'entity.address.street_address',
        label: 'Alamat Jalan',
        placeholder: 'Alamat',
        type: 'text',
        span: 12,
        fullWidth: true,
        validation: {
          required: 'Alamat Jalan harus diisi'
        }
      },
      {
        name: 'entity.address.rt',
        label: 'RT',
        placeholder: 'RT',
        type: 'text',
        span: 2,
        fullWidth: true,
        validation: {
          required: 'RT harus diisi',
          validate: {
            isNumber: (value) => !isNaN(Number(value)) || 'Pastikan format RT sesuai',
            isPositive: (value) => Number(value) > 0 || 'Pastikan format RT sesuai'
          }
        }
      },
      {
        name: 'entity.address.rw',
        label: 'RW',
        placeholder: 'RW',
        type: 'text',
        span: 2,
        fullWidth: true,
        validation: {
          required: 'RW harus diisi',
          validate: {
            isNumber: (value) => !isNaN(Number(value)) || 'Pastikan format RW sesuai',
            isPositive: (value) => Number(value) > 0 || 'Pastikan format RW sesuai'
          }
        }
      },
      {
        name: 'entity.address.country',
        label: 'Negara',
        type: 'text',
        span: 8,
        fullWidth: true,
        disabled: true
      },
      {
        name: 'entity.address.province',
        label: 'Provinsi',
        type: 'autocomplete',
        defaultValue: '',
        placeholder: 'Pilih',
        options: Object.keys(Province).map((item) => {
          return { label: ProvinceMap[item as Province], value: item, key: item };
        }),
        fullWidth: true,
        span: 8,
        validation: {
          required: 'Provinsi harus diisi'
        }
      },
      {
        name: 'entity.address.city',
        label: 'Kota',
        placeholder: 'Kota',
        type: 'autocomplete',
        defaultValue: '',
        options: Object.keys(Dati2).map((item) => {
          return { label: Dati2Map[item as Dati2], value: t(Dati2Map[item as Dati2]) as string, key: item };
        }),
        span: 4,
        fullWidth: true,
        validation: {
          required: 'Kota harus diisi'
        }
      },
      {
        name: 'entity.address.district',
        label: 'Kecamatan',
        placeholder: 'Kecamatan',
        type: 'text',
        span: 4,
        fullWidth: true,
        validation: {
          required: 'Kecamatan harus diisi'
        }
      },
      {
        name: 'entity.address.village',
        label: 'Kelurahan/Desa',
        placeholder: 'Kelurahan/Desa',
        type: 'text',
        span: 4,
        fullWidth: true,
        validation: {
          required: 'Kelurahan/Desa harus diisi'
        }
      },
      {
        name: 'entity.address.postal_code',
        label: 'Kode Pos',
        placeholder: 'Kode Pos',
        type: 'text',
        span: 4,
        fullWidth: true,
        validation: {
          required: 'Kode Pos harus diisi',
          validate: {
            minLength: (value) => value.length === 5 || 'Pastikan format kode pos sesuai, contoh : 10110',
            isNumber: (value) => !isNaN(Number(value)) || 'Pastikan format kode pos sesuai, contoh : 10110',
            isPositive: (value) => Number(value) > 0 || 'Pastikan format kode pos sesuai, contoh : 10110'
          }
        }
      }
    ]
  },
  {
    name: 'entity.mobile_number',
    label: 'Nomor Handphone PIC Perusahaan',
    placeholder: '0000',
    type: 'phonenumber',
    validation: {
      required: 'Nomor Handphone PIC Perusahaan harus diisi',
      validate: {
        isEmpty: (value) => !!value.slice(3) || 'Nomor Handphone PIC Perusahaan harus diisi',
        isNumber: (value) => !isNaN(Number(value)) || 'Pastikan format nomor sesuai',
        isPositive: (value) => Number(value) > 0 || 'Pastikan format nomor sesuai'
      }
    }
  },
  {
    name: 'entity.phone_number',
    label: 'Nomor Telpon Perusahaan',
    placeholder: '0000',
    type: 'phonenumber',
    validation: {
      required: 'Nomor Telpon Perusahaan harus diisi',
      validate: {
        isEmpty: (value) => !!value.slice(3) || 'Nomor Telpon Perusahaan harus diisi',
        isNumber: (value) => !isNaN(Number(value)) || 'Pastikan format nomor sesuai',
        isPositive: (value) => Number(value) > 0 || 'Pastikan format nomor sesuai'
      }
    }
  },
  {
    name: 'entity.account_purpose.purpose',
    label: 'Tujuan Pembukaan Rekening',
    type: 'autocomplete',
    placeholder: 'Pilih',
    defaultValue: '',
    options: Object.keys(AccountPurposePurpose).map((item) => {
      return { label: AccountPurposePurposeMap[item as AccountPurposePurpose], value: item, key: item };
    }),
    validation: {
      required: 'Tujuan Pembukaan Rekening harus diisi'
    }
  },
  {
    conditional: {
      dependOn: 'entity.account_purpose.purpose',
      value: AccountPurposePurpose.OTHER
    },
    name: 'entity.account_purpose.description',
    placeholder: 'Alasan lainnya',
    type: 'text',
    validation: {
      required: 'Alasan lainnya harus diisi'
    }
  },
  {
    name: 'entity.monthly_income',
    label: 'Pendapatan Bulanan',
    placeholder: '0',
    type: 'currency',
    validation: {
      valueAsNumber: true,
      required: 'Pendapatan Bulanan harus diisi',
      validate: {
        isPositive: (value) => Number(value) > 0 || 'Pastikan format sesuai'
      }
    }
  },
  {
    name: 'entity.primary_income_source.type',
    label: 'Sumber Pendapatan Utama',
    type: 'autocomplete',
    placeholder: 'Pilih',
    defaultValue: '',
    options: Object.keys(PrimaryIncomeSourceType).map((item) => {
      return { label: PrimaryIncomeSourceTypeMap[item as PrimaryIncomeSourceType], value: item, key: item };
    }),
    validation: {
      required: 'Sumber Pendapatan Utama harus diisi'
    }
  },
  {
    conditional: {
      dependOn: 'entity.primary_income_source.type',
      value: PrimaryIncomeSourceType.OTHER
    },
    name: 'entity.primary_income_source.description',
    type: 'text',
    placeholder: 'Alasan lainnya',
    validation: {
      required: 'Alasan lainnya harus diisi'
    }
  },
  {
    name: 'entity.estimated_credit_amount',
    label: 'Estimasi Jumlah Uang Ter-Kredit per Tahun',
    placeholder: '0',
    type: 'currency',
    validation: {
      valueAsNumber: true,
      required: 'Estimasi Jumlah Uang Ter-Kredit per Tahun harus diisi',
      validate: {
        isPositive: (value) => Number(value) > 0 || 'Pastikan format sesuai'
      }
    }
  },
  {
    name: 'entity.estimated_debit_amount',
    label: 'Estimasi Jumlah Uang Ter-Debit per Tahun',
    placeholder: '0',
    type: 'currency',
    validation: {
      valueAsNumber: true,
      required: 'Estimasi Jumlah Uang Ter-Debit per Tahun harus diisi',
      validate: {
        isPositive: (value) => Number(value) > 0 || 'Pastikan format sesuai'
      }
    }
  },
  {
    name: 'entity.estimated_credit_frequency',
    label: 'Estimasi Frekuensi Uang Ter-Kredit per Tahun',
    placeholder: '0',
    type: 'number',
    validation: {
      required: 'Estimasi Frekuensi Uang Ter-Kredit per Tahun harus diisi',
      validate: {
        isPositive: (value: number) => Number(value) > 0 || 'Pastikan format sesuai',
        maxLength: (value: number) => value.toString().length <= 3 || 'Tidak melebihi 3 digit'
      }
    }
  },
  {
    name: 'entity.estimated_debit_frequency',
    label: 'Estimasi Frekuensi Uang Ter-Debit per Tahun',
    placeholder: '0',
    type: 'number',
    validation: {
      required: 'Estimasi Frekuensi Uang Ter-Debit per Tahun harus diisi',
      validate: {
        isPositive: (value: number) => Number(value) > 0 || 'Pastikan format sesuai',
        maxLength: (value: number) => value.toString().length <= 3 || 'Tidak melebihi 3 digit'
      }
    }
  },
  {
    name: 'entity.number_of_employees',
    label: 'Jumlah Karyawan',
    placeholder: '0',
    type: 'number',
    validation: {
      required: 'Jumlah Karyawan harus diisi',
      validate: {
        isPositive: (value: number) => Number(value) > 0 || 'Pastikan format sesuai'
      }
    }
  },
  {
    type: 'divider'
  },
  {
    type: 'title',
    label: 'Dokumen Perusahaan'
  },
  {
    name: 'entity.document_ids.npwp',
    type: 'file',
    label: 'NPWP Badan Usaha',
    validation: {
      required: 'NPWP Badan Usaha harus diisi'
    }
  },
  {
    conditional: {
      dependOn: 'entity.izin_usaha.type',
      value: [IzinUsahaType.NIB, IzinUsahaType.SITU, IzinUsahaType.SIUP, IzinUsahaType.TDP]
    },
    name: `entity.document_ids.${(field.value.type ?? '').toLowerCase()}`,
    type: 'file',
    label: 'Izin Usaha',
    validation: {
      required: 'Izin Usaha harus diisi'
    }
  },
  {
    name: 'entity.document_ids.sk_menkumham',
    type: 'file',
    label: 'SK Kemenkumham',
    validation: {
      required: 'SK Kemenkumham harus diisi'
    }
  },
  {
    name: 'entity.first_akta_pendirian_number',
    label: 'No. Akta Pendirian Pertama',
    type: 'text',
    placeholder: '0000000000',
    validation: {
      required: 'No. Akta Pendirian Pertama harus diisi'
    }
  },
  {
    name: 'entity.document_ids.ad_arts',
    type: 'file',
    label: 'AD ART / Sertifikat Pendirian (Semua Versi)',
    allowMultiple: true,
    validation: {
      validate: (value: any) => Boolean(value[0]) || 'AD ART / Sertifikat Pendirian (Semua Versi) harus diisi'
    }
  }
];
